import React, { useMemo, useState } from 'react';
import {
  Container, Card, Table, Form, Badge, Alert, Spinner, Button, ButtonGroup,
} from 'react-bootstrap';
import { useGetBloomBoardQuery } from '../../app/api';

// Exec -> Bloom Board.
//
// Every open rock and to-do across the firm, searchable. Overdue items are split
// into a collapsed "Past" section: they're the minority of what matters day to
// day, but hiding them entirely would lose the accountability the board exists
// for. The server decides what's past — it owns the clock.

const KINDS = [
  { key: 'all', label: 'All' },
  { key: 'rock', label: 'Rocks' },
  { key: 'todo', label: 'To-Dos' },
];

function dueLabel(due) {
  if (!due) return 'No date';
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return due;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysFrom(due) {
  if (!due) return null;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d - new Date()) / 86400000);
}

export default function BloomBoard() {
  const { data, isLoading, isError, error, refetch, isFetching } = useGetBloomBoardQuery({});
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [owner, setOwner] = useState('');
  // Collapsed by default, as asked — past items are context, not the day's work.
  const [showPast, setShowPast] = useState(false);

  const counts = data?.counts || {};
  const current = useMemo(() => data?.current || [], [data]);
  const past = useMemo(() => data?.past || [], [data]);

  const owners = useMemo(() => {
    const set = new Set([...current, ...past].map(i => i.owner));
    return [...set].sort();
  }, [current, past]);

  // One filter for both sections, so a search says the same thing above and
  // below the fold.
  const filter = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows) => rows.filter(r => {
      if (kind !== 'all' && r.kind !== kind) return false;
      if (owner && r.owner !== owner) return false;
      if (!q) return true;
      return (r.title || '').toLowerCase().includes(q)
        || (r.owner || '').toLowerCase().includes(q)
        || (r.meeting || '').toLowerCase().includes(q);
    });
  }, [query, kind, owner]);

  const shownCurrent = filter(current);
  const shownPast = filter(past);

  if (isLoading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
        <p className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>Reading Bloom…</p>
      </Container>
    );
  }
  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error?.data?.detail || 'Could not load the Bloom board.'}</Alert>
      </Container>
    );
  }
  if (data && !data.configured) {
    return (
      <Container className="py-4">
        <h3>Bloom Board</h3>
        <Alert variant="secondary">No Bloom service account is configured.</Alert>
      </Container>
    );
  }

  const table = (rows, isPast) => (
    <Card className="mb-3">
      <Table responsive hover className="mb-0 align-middle">
        <thead>
          <tr>
            <th style={{ width: '44%' }}>Item</th>
            <th style={{ width: '16%' }}>Owner</th>
            <th style={{ width: '22%' }}>Meeting</th>
            <th style={{ width: '18%' }}>Due</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const days = daysFrom(r.due);
            return (
              <tr key={`${r.kind}-${r.id}`}>
                <td>
                  <Badge bg={r.kind === 'rock' ? 'primary' : 'secondary'} className="me-2">
                    {r.kind === 'rock' ? 'Rock' : 'To-Do'}
                  </Badge>
                  {r.url
                    ? <a href={r.url} target="_blank" rel="noopener noreferrer"
                         className="fw-semibold text-decoration-none">{r.title}</a>
                    : <span className="fw-semibold">{r.title}</span>}
                </td>
                <td style={{ fontSize: '0.85rem' }}>{r.owner}</td>
                <td className="text-muted" style={{ fontSize: '0.8rem' }}>{r.meeting}</td>
                <td style={{ fontSize: '0.82rem' }}>
                  <span className={isPast ? 'text-danger fw-semibold' : ''}>{dueLabel(r.due)}</span>
                  {days !== null && (
                    <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                      {isPast
                        ? `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`
                        : days === 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="text-center text-muted py-4">Nothing matches.</td></tr>
          )}
        </tbody>
      </Table>
    </Card>
  );

  return (
    <Container className="py-4">
      <div className="d-flex align-items-start gap-3 flex-wrap mb-1">
        <div style={{ flex: 1, minWidth: '20rem' }}>
          <h3 className="mb-1">Bloom Board</h3>
          <p className="text-muted mb-2">
            Every open rock and to-do across {counts.meetings} meetings.
          </p>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={refetch} disabled={isFetching}>
          {isFetching ? <Spinner animation="border" size="sm" /> : <i className="bi bi-arrow-clockwise" />}
          {' '}Refresh
        </Button>
      </div>

      <div className="d-flex gap-3 flex-wrap mb-3" style={{ fontSize: '0.85rem' }}>
        <span><strong>{counts.rocks}</strong> rocks</span>
        <span className="text-muted">·</span>
        <span><strong>{counts.todos}</strong> to-dos</span>
        <span className="text-muted">·</span>
        <span><strong>{counts.owners}</strong> owners</span>
        <span className="text-muted">·</span>
        <span className="text-danger"><strong>{counts.past}</strong> past due</span>
      </div>

      <div className="d-flex gap-2 flex-wrap mb-3">
        <Form.Control
          style={{ maxWidth: '22rem' }}
          placeholder="Search title, owner or meeting…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <ButtonGroup size="sm">
          {KINDS.map(k => (
            <Button key={k.key}
                    variant={kind === k.key ? 'primary' : 'outline-secondary'}
                    onClick={() => setKind(k.key)}>
              {k.label}
            </Button>
          ))}
        </ButtonGroup>
        <Form.Select
          size="sm"
          style={{ maxWidth: '14rem' }}
          value={owner}
          onChange={e => setOwner(e.target.value)}
        >
          <option value="">All owners</option>
          {owners.map(o => <option key={o} value={o}>{o}</option>)}
        </Form.Select>
      </div>

      <h5 className="mb-2">
        Current
        <span className="text-muted fw-normal" style={{ fontSize: '0.85rem' }}>
          {' '}· {shownCurrent.length}
        </span>
      </h5>
      {table(shownCurrent, false)}

      <Button
        variant="link"
        className="px-0 text-decoration-none"
        onClick={() => setShowPast(v => !v)}
      >
        <i className={`bi bi-chevron-${showPast ? 'down' : 'right'}`} />{' '}
        <strong>Past</strong>
        <span className="text-muted fw-normal">
          {' '}· {shownPast.length} past due
          {shownPast.length !== past.length && ` (of ${past.length})`}
        </span>
      </Button>
      {showPast && table(shownPast, true)}
    </Container>
  );
}
